import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  ArrowLeft,
  Download,
  CheckCircle2,
  ExternalLink,
  ChevronDown,
  Shield,
  Cpu
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useState } from "react";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";

// Update these when cutting a new release
const BROADCASTER_VERSION = "1.0.2";
const R2_BASE_URL = "https://download.flowlabs.live/broadcaster/latest";

const platforms = [
  {
    name: "macOS",
    iconSrc: "/apple-logo.svg",
    available: true,
    version: BROADCASTER_VERSION,
    requirements: "macOS 11.0 (Big Sur) or later",
    architectures: [
      {
        label: "Apple Silicon",
        sublabel: "M1, M2, M3, M4",
        downloadUrl: `${R2_BASE_URL}/macos-arm64.dmg`,
        fileName: `flow-broadcaster-${BROADCASTER_VERSION}-macos-Apple.dmg`,
      },
    ],
  },
  {
    name: "Windows",
    iconSrc: "/windows-logo.svg",
    available: false,
    version: "-",
    requirements: "Windows 10 or later",
    architectures: [],
  },
  {
    name: "Linux",
    iconSrc: "/linux-logo.svg",
    available: false,
    version: "-",
    requirements: "Ubuntu 20.04+ or equivalent",
    architectures: [],
  },
];

const features = [
  "Stream directly to FlowLabs sessions",
  "NDI output support for professional workflows",
  "Multiple scene management",
  "Audio mixing and monitoring",
  "Custom overlays and transitions",
  "Low-latency streaming",
];

const FlowBroadcaster = () => {
  const navigate = useNavigate();
  const [openSource, setOpenSource] = useState(false);

  const handleDownload = (url: string, fileName: string) => {
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName;
    a.click();
  };

  return (
    <div className="container mx-auto px-6 py-8 max-w-5xl">
      <div className="space-y-8">
        {/* Back button */}
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/connectors')}
          className="gap-2 text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Connectors
        </Button>

        {/* Hero Section */}
        <div className="text-center space-y-4 py-8">
          <div className="mx-auto flex justify-center">
            <img
              src="/Logo White Transparent.png"
              alt="Flow Broadcaster"
              className="h-24 w-auto"
            />
          </div>
          <div>
            <div className="flex items-center justify-center gap-3">
              <h1 className="text-4xl font-bold bg-gradient-hero bg-clip-text text-transparent">
                Flow Broadcaster
              </h1>
              <Badge variant="secondary" className="text-sm">v{BROADCASTER_VERSION}</Badge>
            </div>
            <p className="text-muted-foreground mt-2 max-w-lg mx-auto">
              Professional broadcasting software built for FlowLabs.
              Stream your content with advanced controls and seamless integration.
            </p>
          </div>
        </div>

        {/* Download Section */}
        <div className="space-y-4">
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <Download className="w-5 h-5" />
            Download
          </h2>

          <div className="grid md:grid-cols-3 gap-4">
            {platforms.map((platform) => (
              <Card
                key={platform.name}
                className={`p-6 border-border transition-all duration-200 ${
                  platform.available
                    ? 'bg-card'
                    : 'bg-muted/30 opacity-60'
                }`}
              >
                <div className="space-y-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <img
                        src={platform.iconSrc}
                        alt={platform.name}
                        className="w-8 h-8 object-contain"
                      />
                      <div>
                        <h3 className="font-semibold">{platform.name}</h3>
                        <p className="text-xs text-muted-foreground">
                          {platform.available ? `v${platform.version}` : 'Coming Soon'}
                        </p>
                      </div>
                    </div>
                    {!platform.available && (
                      <Badge variant="secondary" className="text-xs">Soon</Badge>
                    )}
                  </div>

                  {platform.available ? (
                    <>
                      <p className="text-xs text-muted-foreground">{platform.requirements}</p>
                      <div className="space-y-2">
                        {platform.architectures.map((arch) => (
                          <Button
                            key={arch.label}
                            variant="outline"
                            className="w-full gap-2 justify-start"
                            size="sm"
                            onClick={() => handleDownload(arch.downloadUrl, arch.fileName)}
                          >
                            <Cpu className="w-3.5 h-3.5 shrink-0" />
                            <span className="flex-1 text-left">{arch.label}</span>
                            <span className="text-xs text-muted-foreground">{arch.sublabel}</span>
                          </Button>
                        ))}
                      </div>
                    </>
                  ) : (
                    <div className="text-sm text-muted-foreground">
                      <p className="text-xs">{platform.requirements}</p>
                    </div>
                  )}
                </div>
              </Card>
            ))}
          </div>
        </div>

        {/* Features */}
        <div className="space-y-4">
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <CheckCircle2 className="w-5 h-5 text-green-500" />
            Features
          </h2>

          <Card className="p-6 bg-card border-border">
            <div className="grid md:grid-cols-2 gap-3">
              {features.map((feature, index) => (
                <div key={index} className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" />
                  <span className="text-sm">{feature}</span>
                </div>
              ))}
            </div>
          </Card>
        </div>

        {/* Quick Start */}
        <div className="space-y-4">
          <h2 className="text-xl font-semibold">Quick Start</h2>

          <Card className="p-6 bg-card border-border">
            <ol className="space-y-4 list-decimal list-inside text-sm">
              <li className="space-y-1">
                <span className="font-medium">Download and install</span>
                <p className="text-muted-foreground ml-5">
                  Download Flow Broadcaster for your Mac (Apple Silicon required).
                </p>
              </li>
              <li className="space-y-1">
                <span className="font-medium">Get your Flow Key</span>
                <p className="text-muted-foreground ml-5">
                  Create a session in FlowLabs and copy your Flow Key from the session dashboard.
                </p>
              </li>
              <li className="space-y-1">
                <span className="font-medium">Configure Flow Broadcaster</span>
                <p className="text-muted-foreground ml-5">
                  Open Settings → Stream, select "FlowLabs" as your service, and paste your Flow Key.
                </p>
              </li>
              <li className="space-y-1">
                <span className="font-medium">Start streaming</span>
                <p className="text-muted-foreground ml-5">
                  Set up your scenes and sources, then click "Start Streaming" to go live!
                </p>
              </li>
            </ol>
          </Card>
        </div>

        {/* Open Source Notice - Collapsible */}
        <Collapsible open={openSource} onOpenChange={setOpenSource}>
          <Card className="p-4 bg-muted/20 border-border">
            <CollapsibleTrigger className="flex items-center justify-between w-full">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Shield className="w-4 h-4" />
                <span>Open Source Software</span>
              </div>
              <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform ${openSource ? 'rotate-180' : ''}`} />
            </CollapsibleTrigger>
            <CollapsibleContent className="pt-4">
              <div className="text-xs text-muted-foreground space-y-2">
                <p>
                  Flow Broadcaster is built on{" "}
                  <a
                    href="https://obsproject.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary hover:underline inline-flex items-center gap-1"
                  >
                    OBS Studio
                    <ExternalLink className="w-3 h-3" />
                  </a>
                  , an open-source project licensed under the{" "}
                  <a
                    href="https://www.gnu.org/licenses/old-licenses/gpl-2.0.html"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary hover:underline"
                  >
                    GNU General Public License v2
                  </a>
                  .
                </p>
                <p>
                  Source code is available upon request. Contact{" "}
                  <a
                    href="mailto:support@flowlabs.live"
                    className="text-primary hover:underline"
                  >
                    support@flowlabs.live
                  </a>
                  {" "}for access.
                </p>
                <p className="text-muted-foreground/60">
                  We thank the OBS Project and its contributors for their excellent work.
                </p>
              </div>
            </CollapsibleContent>
          </Card>
        </Collapsible>
      </div>
    </div>
  );
};

export default FlowBroadcaster;
